// Simple IndexedDB-backed backup for localStorage
// Stores each localStorage entry as { key, value } in an object store named 'kv'
// Provides functions to backup current localStorage and restore it when empty.

export async function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open("app-localstorage-backup", 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("kv")) {
        db.createObjectStore("kv", { keyPath: "key" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  cb: (store: IDBObjectStore) => Promise<T> | T
): Promise<T> {
  const db = await openDB();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction("kv", mode);
    const store = tx.objectStore("kv");
    Promise.resolve(cb(store))
      .then((r) => {
        tx.oncomplete = () => resolve(r);
        tx.onerror = () => reject(tx.error);
      })
      .catch((e) => reject(e));
  });
}

export async function setBackupItem(key: string, value: string): Promise<void> {
  await withStore("readwrite", (store) => {
    store.put({ key, value });
  });
}

export async function getAllBackupItems(): Promise<
  Array<{ key: string; value: string }>
> {
  return withStore("readonly", (store) => {
    return new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () =>
        resolve(req.result as Array<{ key: string; value: string }>);
      req.onerror = () => reject(req.error);
    });
  });
}

export async function clearBackups(): Promise<void> {
  await withStore("readwrite", (store) => {
    store.clear();
  });
}

export async function backupLocalStorage(): Promise<void> {
  try {
    // Iterate keys and persist
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k) keys.push(k);
    }

    await withStore("readwrite", (store) => {
      keys.forEach((k) => {
        const v = localStorage.getItem(k) as string;
        try {
          store.put({ key: k, value: v });
        } catch (e) {
          // ignore per-item errors
          // eslint-disable-next-line no-console
          console.warn("Failed to backup localStorage key", k, e);
        }
      });
    });
    // eslint-disable-next-line no-console
    console.info("localStorage backed up, keys:", keys);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("backupLocalStorage failed", e);
  }
}

export async function restoreLocalStorageIfEmpty(): Promise<void> {
  try {
    if (localStorage.length > 0) return;

    const entries = await getAllBackupItems();
    if (!entries || entries.length === 0) return;

    entries.forEach(({ key, value }) => {
      try {
        localStorage.setItem(key, value);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Failed to restore localStorage key", key, e);
      }
    });
    // eslint-disable-next-line no-console
    console.info(
      "localStorage restored from backup, keys:",
      entries.map((e) => e.key)
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("restoreLocalStorageIfEmpty failed", e);
  }
}

let _intervalId: number | null = null;
export function startPeriodicBackup(intervalMs = 30_000): void {
  if (_intervalId) return;
  // @ts-ignore setInterval returns number in browsers
  _intervalId = window.setInterval(() => {
    backupLocalStorage();
  }, intervalMs) as unknown as number;
  // eslint-disable-next-line no-console
  console.info("Started periodic localStorage backup every", intervalMs, "ms");
}

export function stopPeriodicBackup(): void {
  if (_intervalId) {
    clearInterval(_intervalId);
    _intervalId = null;
    // eslint-disable-next-line no-console
    console.info("Stopped periodic localStorage backup");
  }
}
